import { useId, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { useSession } from '../../workspace/session.js';
import { workspaceCopy } from '../../merchant/copy.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import {
  Button,
  Card,
  DefinitionList,
  PlusIcon,
  SoonBadge,
  Tabs,
  Tag,
  Modal,
  useToast,
} from '../../merchant/ui/primitives.js';

type SettingsTab = 'account' | 'workspaces' | 'language' | 'notifications';
const TABS: readonly SettingsTab[] = [
  'account',
  'workspaces',
  'language',
  'notifications',
];

/**
 * Settings, from the wireframe: Account, Workspaces, Language and
 * Notifications. What the server owns is live (name, sign-out, the
 * interface language); what it does not yet own (editing the name, more
 * workspaces, email preferences) keeps the wireframe's surface, disabled
 * and marked Soon, rather than pretending to persist.
 */
export function MerchantSettingsPage({
  overlayTab,
  onClose,
}: { overlayTab?: string; onClose?: () => void } = {}): ReactNode {
  const { language, toggle } = useLanguage();
  const copy = workspaceCopy(language);
  const { session, logout } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const workspace = useWorkspace();
  const { businessName } = useMerchantWorkspaceIdentity();
  const { tab: routeTab } = useParams<{ tab?: string }>();
  const titleId = useId();
  const requestedTab = overlayTab ?? routeTab;
  const tab: SettingsTab = TABS.includes(requestedTab as SettingsTab)
    ? (requestedTab as SettingsTab)
    : 'account';
  const displayName =
    session.status === 'authenticated' ? session.account.displayName : '';
  const workspaceName = businessName?.trim() || copy.nav.yourBusiness;
  const initial = workspaceName
    .slice(0, 1)
    .toLocaleUpperCase(language === 'ar' ? 'ar-SA' : 'en-US');
  const domain = (() => {
    const website = workspace.profile?.business.sources.find(
      (s) => s.platform === 'WEBSITE',
    );
    if (website === undefined) return '';
    try {
      return new URL(website.canonicalUrl).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  })();

  const content = (
    <section
      className="mj-ws-col"
      aria-labelledby={titleId}
      data-screen-label="Settings"
    >
      <h1 id={titleId} className="mj-ws-vh">
        {copy.settings.title}
      </h1>
      <Tabs
        label={copy.settings.sections}
        active={tab}
        tabs={TABS.map((key) => ({
          key,
          label: copy.settings[key],
          href:
            overlayTab === undefined
              ? `/settings/${key}`
              : (() => {
                  const query = new URLSearchParams(location.search);
                  query.set('settings', key);
                  return `${location.pathname}?${query}`;
                })(),
        }))}
      />

      {tab === 'account' ? (
        <Card role="tabpanel" className="mj-ws-col" padding={24}>
          <DefinitionList
            rows={[
              [copy.settings.name, displayName || copy.common.dash],
              [copy.settings.signedInWith, copy.settings.google],
            ]}
          />
          <div className="mj-ws-row">
            <Button small disabled title={copy.settings.editNameSoon}>
              {copy.settings.editName}
            </Button>
            <SoonBadge />
            <Button
              variant="quiet"
              small
              onClick={() => {
                void logout().finally(() => navigate('/', { replace: true }));
              }}
            >
              {copy.settings.signOut}
            </Button>
          </div>
        </Card>
      ) : null}

      {tab === 'workspaces' ? (
        <Card role="tabpanel" padding={0}>
          <div className="mj-ws-orow">
            <span className="mj-ws-wsav" aria-hidden="true">
              {initial}
            </span>
            <div className="mj-ws-orow-copy">
              <div className="mj-ws-strong">{workspaceName}</div>
              <div className="mj-ws-muted mj-ws-sm">
                {domain ? (
                  <>
                    <span dir="ltr">{domain}</span> ·{' '}
                  </>
                ) : null}
                {copy.common.linesCount(workspace.lines.length)}
              </div>
            </div>
            <Tag>{copy.settings.currentWs}</Tag>
          </div>
          <div className="mj-ws-card-foot mj-ws-row">
            <Button small disabled title={copy.settings.createWsSoon}>
              <PlusIcon />
              {copy.settings.createWs}
            </Button>
            <SoonBadge />
            <span className="mj-ws-muted mj-ws-sm">{copy.settings.createWsSoon}</span>
          </div>
        </Card>
      ) : null}

      {tab === 'language' ? (
        <Card role="tabpanel" padding={24}>
          <fieldset>
            <legend className="mj-ws-legend">{copy.settings.interfaceLanguage}</legend>
            <label className="mj-ws-radio">
              <input
                type="radio"
                name="lang"
                value="en"
                checked={language === 'en'}
                onChange={() => {
                  if (language !== 'en') toggle();
                }}
              />
              <span>{copy.settings.english}</span>
            </label>
            <label className="mj-ws-radio">
              <input
                type="radio"
                name="lang"
                value="ar"
                checked={language === 'ar'}
                onChange={() => {
                  if (language !== 'ar') toggle();
                }}
              />
              <span lang="ar">{copy.settings.arabic}</span>
            </label>
          </fieldset>
          <p className="mj-ws-muted mj-ws-sm mj-ws-mt-12">
            {copy.settings.languageNote}
          </p>
        </Card>
      ) : null}

      {tab === 'notifications' ? (
        <Card role="tabpanel" padding={24}>
          <div className="mj-ws-row mj-ws-mt-8" style={{ marginBottom: 12 }}>
            <SoonBadge />
            <span className="mj-ws-muted mj-ws-sm">{copy.settings.notifSoon}</span>
          </div>
          {(
            [
              ['ready', copy.settings.notifReady, copy.settings.notifReadyBody, true],
              ['input', copy.settings.notifInput, copy.settings.notifInputBody, true],
              [
                'weekly',
                copy.settings.notifWeekly,
                copy.settings.notifWeeklyBody,
                false,
              ],
            ] as const
          ).map(([key, title, body, on]) => (
            <label key={key} className="mj-ws-sw">
              <span>
                <span className="mj-ws-strong" style={{ display: 'block' }}>
                  {title}
                </span>
                <span className="mj-ws-muted mj-ws-sm">{body}</span>
              </span>
              <input
                type="checkbox"
                role="switch"
                checked={on}
                disabled
                aria-describedby={`${titleId}-notif-soon`}
                onChange={() => {
                  toast(copy.settings.notifSoon);
                }}
              />
            </label>
          ))}
          <span id={`${titleId}-notif-soon`} className="mj-ws-vh">
            {copy.settings.notifSoon}
          </span>
        </Card>
      ) : null}
    </section>
  );
  const close =
    onClose ??
    (() => {
      void navigate('/home', { replace: true });
    });
  return (
    <Modal
      title={copy.settings.title}
      onClose={close}
      footer={<Button onClick={close}>{copy.common.close}</Button>}
    >
      {content}
    </Modal>
  );
}
